import React, { useState } from 'react';
import { Plus, Minus, HelpCircle, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FAQS } from '../data/site';

/**
 * Reusable FAQ accordion. Used on the home page and on the dedicated /faq page.
 * `variant="preview"` shows a heading + link to the full FAQ page.
 * `variant="full"` shows every FAQ open by default.
 */
export const FAQ = ({ variant = 'preview' }) => {
  const [openIndex, setOpenIndex] = useState(variant === 'full' ? -1 : 0);

  const toggle = (i) => setOpenIndex(openIndex === i ? -1 : i);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-20 sm:py-28 bg-[#0A0A0A] border-y border-[#E8B842]/15"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
            / Frequently asked questions
          </span>
          <h2 className="font-display uppercase font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-4">
            Common <span className="text-[#E8B842]">questions</span>, honest answers
          </h2>
          <p className="mt-6 text-[#A3A3A3] text-base sm:text-lg max-w-2xl mx-auto">
            The questions we get asked the most by homeowners in Leamington Spa and the
            surrounding villages.
          </p>
        </div>

        <div className="mt-12 divide-y divide-[#E8B842]/15 border border-[#E8B842]/15 bg-[#0E0E0E]">
          {FAQS.map((f, i) => {
            const open = openIndex === i;
            return (
              <div key={f.q} data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  aria-expanded={open}
                  className="w-full flex items-start justify-between gap-6 text-left px-5 sm:px-7 py-5 sm:py-6 hover:bg-[#111] transition-colors"
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <HelpCircle className="w-5 h-5 text-[#E8B842] shrink-0 mt-1" />
                    <h3 className="font-display uppercase tracking-wider text-white text-base sm:text-lg font-bold leading-snug">
                      {f.q}
                    </h3>
                  </div>
                  <span className="shrink-0 w-8 h-8 bg-[#E8B842]/10 border border-[#E8B842]/30 flex items-center justify-center text-[#E8B842] transition-colors">
                    {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-300 ease-out ${
                    open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 sm:px-7 pb-6 pl-14 sm:pl-16 text-[#C7C7C7] text-sm sm:text-base leading-relaxed">
                      {f.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {variant === 'preview' && (
          <div className="mt-10 text-center">
            <Link
              to="/faq"
              data-testid="faq-see-all"
              className="inline-flex items-center gap-2 border border-[#E8B842]/40 text-[#E8B842] hover:bg-[#E8B842] hover:text-black font-display font-bold uppercase tracking-wider px-7 py-3.5 transition-colors"
            >
              See the full FAQ page
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQ;
